import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download, Paperclip, Send, X } from 'lucide-react';
import { Botao } from '../../../web/src/componentes/base';
import { Estado } from '../../../web/src/componentes/estados';
import { useToast } from '../../../web/src/componentes/toast';
import { mensagemDeErro } from '../../../web/src/nucleo/api';
import { dataHora, nomeDeArquivo, textoLivre } from '../../../web/src/nucleo/formato';
import { baixar, chamar, pget, ppost } from '../api';
import { useSessao } from '../sessao';
import { tamanhoArquivo } from './comum';

interface Mensagem {
  id: string;
  direcao: 'entrada' | 'saida';
  texto: string;
  autor_nome: string | null;
  estado: 'pendente' | 'entregue' | 'lida' | 'falhou';
  created_at: string;
  /** Trilha 4: arquivo da mensagem (nos dois sentidos). */
  arquivo: { nome: string; mime: string; tamanho: number } | null;
}

/** Mensagens com a equipe (canal "Portal" do Conversas): a resposta aparece sozinha pelo tempo real. */
export function Mensagens() {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const toast = useToast();
  const fim = useRef<HTMLDivElement>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const q = useQuery({ queryKey: ['portal', 'mensagens'], queryFn: () => pget<{ itens: Mensagem[] }>('/mensagens'), enabled: s.secoes.mensagens });
  const novas = q.data?.itens.filter((m) => m.direcao === 'saida' && m.estado !== 'lida').length ?? 0;
  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'end' });
    if (novas && !s.somente_leitura) void ppost('/mensagens/lidas').then(() => qc.invalidateQueries({ queryKey: ['portal', 'hoje'] }));
  }, [q.data, novas, s.somente_leitura, qc]);
  // Trilha 4: com arquivo, vai pelo envio de arquivo do chat (o texto é a legenda); sem, o texto como antes.
  const enviar = useMutation({
    mutationFn: () => {
      if (!arquivo) return ppost('/mensagens', { texto: texto.trim() });
      const f = new FormData();
      f.set('texto', texto.trim());
      f.set('arquivo', arquivo, arquivo.name);
      return chamar('POST', '/mensagens/arquivo', f);
    },
    onSuccess: async () => {
      setTexto('');
      setArquivo(null);
      setErro(null);
      await qc.invalidateQueries({ queryKey: ['portal', 'mensagens'] });
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  if (!s.secoes.mensagens) return <p className="rounded-lg border border-borda bg-sup-1 p-4 text-sm text-tinta-2">{t('mensagens.indisponivel')}</p>;
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('mensagens.titulo')}</h1>
        <p className="mt-1 text-sm text-tinta-2">{t('mensagens.texto')}</p>
      </div>
      <section className="flex min-h-80 flex-col rounded-xl border border-borda bg-sup-1">
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4" aria-live="polite" data-mensagens>
          <Estado consulta={q}>
            {(d) =>
              d.itens.length ? (
                <>
                  {d.itens.map((m) => (
                    <div key={m.id} className={`flex max-w-bolha flex-col gap-1 ${m.direcao === 'entrada' ? 'self-end items-end' : 'self-start items-start'}`} data-mensagem={m.direcao}>
                      <span className="text-xs text-tinta-3">{m.direcao === 'entrada' ? t('mensagens.voce') : m.autor_nome ? `${m.autor_nome} · ${t('mensagens.equipe')}` : t('mensagens.equipe')}</span>
                      {m.texto && (
                        <p className={`rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.direcao === 'entrada' ? 'bg-acento-suave text-tinta-1' : 'border border-borda bg-sup-2 text-tinta-1'}`}>{textoLivre(m.texto, s.empresa.nome)}</p>
                      )}
                      {m.arquivo && (
                        <Botao
                          tamanho="sm"
                          icone={<Download className="size-4" aria-hidden />}
                          aria-label={t('mensagens.baixarAnexo', { nome: nomeDeArquivo(m.arquivo.nome, s.empresa.nome) })}
                          data-anexo-mensagem=""
                          onClick={async () => {
                            try {
                              await baixar(`/mensagens/${m.id}/arquivo`, m.arquivo!.nome);
                            } catch (err) {
                              toast.erro(mensagemDeErro(err));
                            }
                          }}
                        >
                          <span className="max-w-48 truncate">{nomeDeArquivo(m.arquivo.nome, s.empresa.nome)}</span>
                          <span className="numero text-xs text-tinta-3">{tamanhoArquivo(m.arquivo.tamanho)}</span>
                        </Botao>
                      )}
                      <span className="text-xs text-tinta-3">
                        {dataHora(m.created_at)}
                        {m.direcao === 'entrada' ? ` · ${t(`mensagens.estado.${m.estado}`)}` : ''}
                      </span>
                    </div>
                  ))}
                  <div ref={fim} />
                </>
              ) : (
                <p className="m-auto text-sm text-tinta-3">{t('mensagens.vazia')}</p>
              )
            }
          </Estado>
        </div>
        <form
          className="flex items-end gap-2 border-t border-borda p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (texto.trim() || arquivo) enviar.mutate();
          }}
        >
          <input
            ref={seletor}
            type="file"
            accept="application/pdf,image/*,.xml,.csv,.txt,.doc,.docx,.xls,.xlsx,.odt,.ods"
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => {
              setArquivo(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Botao
            aria-label={t('mensagens.anexar')}
            title={`${t('mensagens.anexar')}. ${t('mensagens.anexarAjuda')}`}
            disabled={s.somente_leitura}
            icone={<Paperclip className="size-4" aria-hidden />}
            onClick={() => seletor.current?.click()}
          />
          <label htmlFor="mensagem-texto" className="sr-only">
            {t('mensagens.escrever')}
          </label>
          <textarea
            id="mensagem-texto"
            rows={2}
            maxLength={4096}
            value={texto}
            disabled={s.somente_leitura}
            placeholder={t('mensagens.escrever')}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && (texto.trim() || arquivo)) enviar.mutate();
            }}
            className="min-h-10 flex-1 resize-none rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 placeholder:text-tinta-3"
          />
          <Botao type="submit" variante="primario" carregando={enviar.isPending} disabled={(!texto.trim() && !arquivo) || s.somente_leitura} icone={<Send className="size-4" aria-hidden />}>
            {t('mensagens.enviar')}
          </Botao>
        </form>
        {arquivo && (
          <p className="flex min-w-0 items-center gap-2 px-3 pb-3 text-sm text-tinta-1" data-arquivo-escolhido>
            <span className="truncate">{arquivo.name}</span>
            <span className="numero shrink-0 text-xs text-tinta-3">{tamanhoArquivo(arquivo.size)}</span>
            <button type="button" aria-label={t('mensagens.removerAnexo', { nome: arquivo.name })} onClick={() => setArquivo(null)} className="rounded-full p-0.5 text-tinta-3 hover:text-tinta-1">
              <X className="size-3.5" aria-hidden />
            </button>
          </p>
        )}
        {erro && (
          <p role="alert" className="px-3 pb-3 text-sm text-perigo">
            {erro}
          </p>
        )}
      </section>
    </div>
  );
}
